/* EditImageComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./EditImageRenderer");

class EditImageComponent extends EventEmitter {
    constructor(entity, logger) {
        super();
        this._entity = entity;
        this._logger = logger;
        this._titleText = "Edit Image";

        this._linkStatus = entity.linkStatus || "";
        this._validationError = null;

        this._renderer = new Renderer(this);

        this.reject = this.reject.bind(this);
        this.resolve = this.resolve.bind(this);

        this._registerListeners();
        this._renderer.render();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/
    _registerListeners() {}

    _unregisterListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/
    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get titleText() {
        return this._titleText;
    }

    get entity() {
        return this._entity;
    }

    get name() {
        return this._entity.getName();
    }

    get description() {
        return this._entity.getDescription();
    }

    get url() {
        return this._entity.getUrl();
    }

    get linkStatus() {
        return this._linkStatus;
    }

    get logger() {
        return this._logger;
    }

    get validationError() {
        return this._validationError;
    }

    setValidationError(error) {
        this._validationError = error;
        this.emit("ValidationError", error);
    }

    clearValidationError() {
        this._validationError = null;
        this.emit("ValidationError", null);
    }

    setName(value) {
        this._entity.setName(value);
        this.emit("FormChanged");
    }

    setDescription(value) {
        this._entity.setDescription(value);
        this.emit("FormChanged");
    }

    setUrl(value) {
        this._entity.setUrl(value);
        this.emit("FormChanged");
    }

    hasRequiredFields() {
        const name = this._entity.getName();
        const url = this._entity.getUrl();
        const isExternal = this._linkStatus === "EXTERNAL";

        // Name is required
        if (!name || name.trim().length === 0) {
            return false;
        }

        // URL is required unless linkStatus is EXTERNAL
        if (!isExternal && (!url || url.trim().length === 0)) {
            return false;
        }

        return true;
    }

    isUrlValid() {
        const url = this._entity.getUrl();

        // If URL is provided, validate format
        if (url && url.trim().length > 0) {
            const urlPattern = /^https?:\/\/.+/i;
            return urlPattern.test(url.trim());
        }

        return true;
    }

    isFormValid() {
        return this.hasRequiredFields() && this.isUrlValid();
    }

    isFormDirty() {
        return this._entity.isDirty();
    }

    canSave() {
        // Save button enabled if required fields are filled and entity is dirty
        // URL format validation happens on save click
        return this.hasRequiredFields() && this.isFormDirty();
    }

    shutdown() {
        this._unregisterListeners();
        return this._renderer.shutdown().then(
            () => {
                this._renderer = undefined;
                this._entity = undefined;
                this._logger = undefined;
            },
            (err) => {
                if (this._logger) {
                    this._logger.warn({error: err}, "EditImageComponent.shutdown() error");
                }
            }
        );
    }

    cancelAction() {
        const err = new Error("user rejected");
        err.userRejected = true;
        return this.reject(err);
    }

    validateAndSave() {
        // Clear any previous validation errors
        this.clearValidationError();

        // Validate URL format
        if (!this.isUrlValid()) {
            this.setValidationError("Enter a valid URL starting with http:// or https://.");
            return;
        }

        // All validations passed - return success
        return this.resolve({
            saved: true,
        });
    }

    reject(err) {
        this._context.reject(err);
    }

    resolve(action) {
        this._context.resolve(action);
    }

    // prettier-ignore
    set "$teve.ui.context" (context) { //jshint ignore:line
        this._context = context;
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /*******************************************/
}

module.exports = EditImageComponent;
